import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  TRAINING_FRAMEWORKS,
  TrainingFramework,
} from '@/mock/gpu-instance';
import { Target, HeartPulse, AudioWaveform, Sparkles, GraduationCap } from 'lucide-react';

interface TrainingConfigSelectorProps {
  selectedFramework: string;
  onFrameworkSelect: (frameworkId: string) => void;
  disabled?: boolean;
}

const getFrameworkIcon = (icon: TrainingFramework['icon']) => {
  switch (icon) {
    case 'Target':
      return <Target className="h-4 w-4 text-green-500" />;
    case 'HeartPulse':
      return <HeartPulse className="h-4 w-4 text-red-500" />;
    case 'AudioWaveform':
      return <AudioWaveform className="h-4 w-4 text-blue-500" />;
    case 'Sparkles':
      return <Sparkles className="h-4 w-4 text-purple-500" />;
    default:
      return null;
  }
};

const TrainingConfigSelector = ({
  selectedFramework,
  onFrameworkSelect,
  disabled = false,
}: TrainingConfigSelectorProps) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Label className="text-base font-medium flex items-center gap-2">
            <GraduationCap className="h-4 w-4" />
            Training Framework
          </Label>
          <p className="text-sm text-muted-foreground">
            Select the deep learning framework for training
          </p>
        </div>
      </div>

      <RadioGroup
        value={selectedFramework}
        onValueChange={onFrameworkSelect}
        disabled={disabled}
        className="grid grid-cols-2 gap-3"
      >
        {TRAINING_FRAMEWORKS.map((framework) => (
          <Card
            key={framework.id}
            className={`cursor-pointer transition-colors ${
              selectedFramework === framework.id
                ? 'border-primary bg-primary/5'
                : 'hover:border-muted-foreground/50'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => !disabled && onFrameworkSelect(framework.id)}
          >
            <CardContent className="flex flex-col items-center gap-2 p-4">
              <RadioGroupItem
                value={framework.id}
                id={`framework-${framework.id}`}
                disabled={disabled}
                className="sr-only"
              />
              <div className="flex items-center gap-2">
                {getFrameworkIcon(framework.icon)}
                <label
                  htmlFor={`framework-${framework.id}`}
                  className="font-medium cursor-pointer"
                >
                  {framework.name}
                </label>
              </div>
              <Badge variant="outline" className="text-xs">
                v{framework.version}
              </Badge>
              <p className="text-xs text-muted-foreground text-center">
                {framework.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </RadioGroup>
    </div>
  );
};

export default TrainingConfigSelector;
